import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Select, MenuItem, Chip,
  IconButton, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, useMediaQuery, useTheme, Tabs, Tab, FormControl,
  InputLabel, Autocomplete, Card, CardContent, Stack, Alert,
  Avatar, Badge, Snackbar
} from '@mui/material';
import { Edit, Delete, Add, Remove, PhotoCamera, Clear, FilterList, CloudDownload, OpenInNew } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../api/axiosConfig';
import { getMediaUrl } from '../../utils/mediaUrl';
import ImportFromReference from './ImportFromReference';

const CompetitionsManagement = () => {
  const { t } = useTranslation();

  // États
  const [activeTab, setActiveTab] = useState(0);

  // Niveaux
  const [niveaux, setNiveaux] = useState([]);
  const [niveauForm, setNiveauForm] = useState({ nom: '', ordre: 1 });
  const [editingNiveau, setEditingNiveau] = useState(null);
  
  // Compétitions
  const [competitions, setCompetitions] = useState([]);
  const [competitionForm, setCompetitionForm] = useState({ nom: '', niveau_id: '', eurobasket_url: '' });
  const [editingCompetition, setEditingCompetition] = useState(null);
  const [competitionLogo, setCompetitionLogo] = useState(null);
  const [competitionLogoPreview, setCompetitionLogoPreview] = useState(null);
  
  // Équipes
  const [equipes, setEquipes] = useState([]);
  const [equipeForm, setEquipeForm] = useState({ nom: '', competition_ids: [] });
  const [editingEquipe, setEditingEquipe] = useState(null);
  const [equipeLogo, setEquipeLogo] = useState(null);
  const [equipeLogoPreview, setEquipeLogoPreview] = useState(null);
  
  // Joueurs (pour la validation de suppression d'équipes)
  const [joueurs, setJoueurs] = useState([]);
  
  // Associations (gardé pour l'affichage des équipes)
  const [equipeCompetitions, setEquipeCompetitions] = useState([]);
  
  // Filtres Competitions
  const [competitionTextFilter, setCompetitionTextFilter] = useState('');
  const [competitionNiveauFilter, setCompetitionNiveauFilter] = useState([]);
  
  // Filtres Equipes
  const [equipeTextFilter, setEquipeTextFilter] = useState('');
  const [equipeCompetitionFilter, setEquipeCompetitionFilter] = useState([]);
  
  // UI
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Import dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importMode, setImportMode] = useState('ligue'); // 'ligue' ou 'equipes'

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // =============== FETCH DATA ===============
  
  const fetchNiveaux = async () => {
    try {
      const { data } = await api.get('/api/comp/niveaux');
      setNiveaux(data);
    } catch (err) {
      console.error('Error al recuperar los niveles:', err);
    }
  };

  const fetchCompetitions = async () => {
    try {
      const { data } = await api.get('/api/comp/competitions');
      setCompetitions(data);
    } catch (err) {
      console.error('Error al recuperar las competiciones:', err);
    }
  };

  const fetchEquipes = async () => {
    try {
      const { data } = await api.get('/api/comp/equipes');
      setEquipes(data);
    } catch (err) {
      console.error('Error al recuperar los equipos:', err);
    }
  };

  const fetchEquipeCompetitions = async () => {
    try {
      const { data } = await api.get('/api/comp/equipe-competitions');
      setEquipeCompetitions(data);
    } catch (err) {
      console.error('Error al recuperar las asociaciones:', err);
    }
  };

  const fetchJoueurs = async () => {
    try {
      const { data } = await api.get('/api/comp/joueurs');
      setJoueurs(data);
    } catch (err) {
      console.error('Error al recuperar los jugadores:', err);
    }
  };

  useEffect(() => {
    fetchNiveaux();
    fetchCompetitions();
    fetchEquipes();
    fetchEquipeCompetitions();
    fetchJoueurs();
  }, []);

  // =============== FILTRES ===============
  
  // Filtre pour les compétitions
  const getFilteredCompetitions = () => {
    return competitions.filter(competition => {
      const matchesText = competitionTextFilter === '' || 
        competition.nom.toLowerCase().includes(competitionTextFilter.toLowerCase()) ||
        competition.niveau_nom.toLowerCase().includes(competitionTextFilter.toLowerCase());
      
      const matchesNiveau = competitionNiveauFilter.length === 0 || 
        competitionNiveauFilter.includes(competition.niveau_id);
      
      return matchesText && matchesNiveau;
    });
  };
  
  // Filtre pour les équipes
  const getFilteredEquipes = () => {
    return equipes.filter(equipe => {
      // Filtre texte sur nom d'équipe et compétitions
      const matchesText = equipeTextFilter === '' || 
        equipe.nom.toLowerCase().includes(equipeTextFilter.toLowerCase()) ||
        (equipe.competitions && equipe.competitions.toLowerCase().includes(equipeTextFilter.toLowerCase()));
      
      // Filtre sur les compétitions sélectionnées
      if (equipeCompetitionFilter.length === 0) return matchesText;
      
      const equipeCompIds = equipeCompetitions
        .filter(ec => ec.equipe_id === equipe.id)
        .map(ec => ec.competition_id);
      
      const matchesCompetition = equipeCompetitionFilter.some(compId => 
        equipeCompIds.includes(compId)
      );
      
      return matchesText && matchesCompetition;
    });
  };

  // =============== LOGOS HANDLING ===============

  const handleLogoChange = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      if (type === 'competition') {
        setCompetitionLogo(file);
        setCompetitionLogoPreview(URL.createObjectURL(file));
      } else {
        setEquipeLogo(file);
        setEquipeLogoPreview(URL.createObjectURL(file));
      }
    }
  };

  const uploadLogo = async (id, type, file) => {
    const formData = new FormData();
    formData.append('logo', file);

    try {
      await api.post(`/api/comp/${type}/${id}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } catch (err) {
      console.error('Error uploading logo:', err);
      throw err;
    }
  };

  const deleteLogo = async (id, type) => {
    try {
      await api.delete(`/api/comp/${type}/${id}/logo`);
    } catch (err) {
      console.error('Error deleting logo:', err);
    }
  };

  const getLogoUrl = (id, type) => {
    return getMediaUrl(type, `${id}.jpg`, true);
  };

  // =============== NIVEAUX CRUD ===============
  
  const handleAddNiveau = async () => {
    if (!niveauForm.nom) return;
    try {
      const { data } = await api.post('/api/comp/niveaux', {
        ...niveauForm
      });
      setNiveaux([...niveaux, data]);
      setNiveauForm({ nom: '', ordre: 1 });
      setSuccessMessage(t('competitions.messages.levelAdded'));
    } catch (err) {
      setError(t('competitions.messages.addLevelError'));
    }
  };

  const handleUpdateNiveau = async () => {
    try {
      await api.put(`/api/comp/niveaux/${editingNiveau.id}`, {
        ...niveauForm
      });
      fetchNiveaux();
      setNiveauForm({ nom: '', ordre: 1 });
      setEditingNiveau(null);
      setSuccessMessage(t('competitions.messages.levelUpdated'));
    } catch (err) {
      setError(t('competitions.messages.updateLevelError'));
    }
  };

  const handleDeleteNiveau = async (niveau) => {
    try {
      // Vérifier s'il y a des compétitions dans ce niveau
      const competitionsInNiveau = competitions.filter(c => c.niveau_id === niveau.id);
      if (competitionsInNiveau.length > 0) {
        setError(t('competitions.messages.levelDeleteError', { name: niveau.nom, count: competitionsInNiveau.length }));
        setConfirmDelete(null);
        return;
      }

      await api.delete(`/api/comp/niveaux/${niveau.id}`);
      fetchNiveaux();
      fetchCompetitions();
      setConfirmDelete(null);
      setSuccessMessage(t('competitions.messages.levelDeleted'));
    } catch (err) {
      setError(err.response?.data?.message || t('competitions.messages.deleteLevelError'));
    }
  };

  // =============== COMPETITIONS CRUD ===============

  const handleAddCompetition = async () => {
    if (!competitionForm.nom || !competitionForm.niveau_id) {
      setError(t('competitions.competitionsTab.requiredFieldsError'));
      return;
    }
    try {
      const { data } = await api.post('/api/comp/competitions', {
        ...competitionForm
      });

      // Upload logo si présent
      if (competitionLogo) {
        await uploadLogo(data.id, 'competitions', competitionLogo);
      }

      setCompetitions([...competitions, data]);
      setCompetitionForm({ nom: '', niveau_id: '', eurobasket_url: '' });
      setCompetitionLogo(null);
      setCompetitionLogoPreview(null);
      setSuccessMessage(t('competitions.messages.competitionAdded'));
    } catch (err) {
      setError(t('competitions.messages.addCompetitionError'));
    }
  };

  const handleUpdateCompetition = async () => {
    if (!competitionForm.nom || !competitionForm.niveau_id) {
      setError(t('competitions.competitionsTab.requiredFieldsError'));
      return;
    }
    try {
      await api.put(`/api/comp/competitions/${editingCompetition.id}`, {
        ...competitionForm
      });

      // Upload nouveau logo si présent
      if (competitionLogo) {
        await uploadLogo(editingCompetition.id, 'competitions', competitionLogo);
      }

      fetchCompetitions();
      setCompetitionForm({ nom: '', niveau_id: '', eurobasket_url: '' });
      setEditingCompetition(null);
      setCompetitionLogo(null);
      setCompetitionLogoPreview(null);
      setSuccessMessage(t('competitions.messages.competitionUpdated'));
    } catch (err) {
      setError(t('competitions.messages.updateCompetitionError'));
    }
  };

  const handleDeleteCompetition = async (competition) => {
    try {
      // Vérifier s'il y a des équipes dans cette compétition
      const equipesInCompetition = equipeCompetitions.filter(ec => ec.competition_id === competition.id);
      if (equipesInCompetition.length > 0) {
        setError(t('competitions.messages.competitionDeleteError', { name: competition.nom, count: equipesInCompetition.length }));
        setConfirmDelete(null);
        return;
      }

      await api.delete(`/api/comp/competitions/${competition.id}`);
      // Supprimer le logo aussi
      await deleteLogo(competition.id, 'competitions');
      fetchCompetitions();
      fetchEquipeCompetitions();
      setConfirmDelete(null);
      setSuccessMessage(t('competitions.messages.competitionDeleted'));
    } catch (err) {
      setError(t('competitions.messages.deleteCompetitionError'));
    }
  };

  // =============== EQUIPES CRUD ===============

  const handleAddEquipe = async () => {
    if (!equipeForm.nom || equipeForm.competition_ids.length === 0) {
      setError(t('competitions.teams.selectCompetitionError'));
      return;
    }
    try {
      // Créer l'équipe
      const { data: newEquipe } = await api.post('/api/comp/equipes', {
        nom: equipeForm.nom
      });

      // Upload logo si présent
      if (equipeLogo) {
        await uploadLogo(newEquipe.id, 'equipes', equipeLogo);
      }

      // Associer aux compétitions
      const associations = equipeForm.competition_ids.map(comp_id =>
        api.post('/api/comp/equipe-competitions', {
          equipe_id: newEquipe.id,
          competition_id: comp_id
        })
      );

      await Promise.all(associations);

      // Actualiser les données
      fetchEquipes();
      fetchEquipeCompetitions();
      setEquipeForm({ nom: '', competition_ids: [] });
      setEquipeLogo(null);
      setEquipeLogoPreview(null);
      setSuccessMessage(t('competitions.messages.teamAdded'));
    } catch (err) {
      setError(t('competitions.messages.addTeamError'));
    }
  };

  const handleUpdateEquipe = async () => {
    if (!equipeForm.nom || equipeForm.competition_ids.length === 0) {
      setError(t('competitions.teams.selectCompetitionError'));
      return;
    }
    try {
      // Mettre à jour le nom
      await api.put(`/api/comp/equipes/${editingEquipe.id}`, {
        nom: equipeForm.nom
      });

      // Upload nouveau logo si présent
      if (equipeLogo) {
        await uploadLogo(editingEquipe.id, 'equipes', equipeLogo);
      }

      // Obtenir les associations actuelles
      const currentAssociations = equipeCompetitions
        .filter(ec => ec.equipe_id === editingEquipe.id)
        .map(ec => ec.competition_id);

      // Supprimer les associations qui ne sont plus sélectionnées
      const toDelete = currentAssociations.filter(id => !equipeForm.competition_ids.includes(id));
      for (const comp_id of toDelete) {
        await api.delete(`/api/comp/equipe-competitions/${editingEquipe.id}/${comp_id}`);
      }

      // Ajouter les nouvelles associations
      const toAdd = equipeForm.competition_ids.filter(id => !currentAssociations.includes(id));
      for (const comp_id of toAdd) {
        await api.post('/api/comp/equipe-competitions', {
          equipe_id: editingEquipe.id,
          competition_id: comp_id
        });
      }

      fetchEquipes();
      fetchEquipeCompetitions();
      setEquipeForm({ nom: '', competition_ids: [] });
      setEditingEquipe(null);
      setEquipeLogo(null);
      setEquipeLogoPreview(null);
      setSuccessMessage(t('competitions.messages.teamUpdated'));
    } catch (err) {
      setError(t('competitions.messages.updateTeamError'));
    }
  };

  const handleDeleteEquipe = async (equipe) => {
    try {
      // Vérifier s'il y a des joueurs dans cette équipe
      const joueursInEquipe = joueurs.filter(j => j.equipe_id === equipe.id);
      if (joueursInEquipe.length > 0) {
        setError(t('competitions.messages.teamDeleteError', { name: equipe.nom, count: joueursInEquipe.length }));
        setConfirmDelete(null);
        return;
      }

      await api.delete(`/api/comp/equipes/${equipe.id}`);
      // Supprimer le logo aussi
      await deleteLogo(equipe.id, 'equipes');
      fetchEquipes();
      fetchEquipeCompetitions();
      setConfirmDelete(null);
      setSuccessMessage(t('competitions.messages.teamDeleted'));
    } catch (err) {
      setError(err.response?.data?.message || t('competitions.messages.deleteTeamError'));
    }
  };

  // =============== RENDER TABS ===============

  const renderNiveauxTab = () => (
    <Box>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t('competitions.levels.name')}
            value={niveauForm.nom}
            onChange={(e) => setNiveauForm({ ...niveauForm, nom: e.target.value })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label={t('competitions.levels.order')}
            type="number"
            value={niveauForm.ordre}
            onChange={(e) => setNiveauForm({ ...niveauForm, ordre: parseInt(e.target.value) || 1 })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Button
            variant="contained"
            onClick={editingNiveau ? handleUpdateNiveau : handleAddNiveau}
            sx={{ backgroundColor: '#ff9800', height: '56px' }}
            fullWidth
          >
            {editingNiveau ? t('competitions.levels.update') : t('competitions.levels.add')}
          </Button>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('competitions.levels.level')}</TableCell>
              <TableCell>{t('competitions.levels.order')}</TableCell>
              <TableCell align="right">{t('competitions.levels.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {niveaux.map((niveau) => (
              <TableRow key={niveau.id}>
                <TableCell>{niveau.nom}</TableCell>
                <TableCell>{niveau.ordre}</TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => {
                      setEditingNiveau(niveau);
                      setNiveauForm({ nom: niveau.nom, ordre: niveau.ordre });
                    }}
                    sx={{ color: '#ff9800' }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    onClick={() => setConfirmDelete({ type: 'niveau', item: niveau })}
                    sx={{ color: 'red' }}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderCompetitionsTab = () => {
    const filteredCompetitions = getFilteredCompetitions();

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={5}>
            <TextField
              label={t('competitions.competitionsTab.name')}
              value={competitionForm.nom}
              onChange={(e) => setCompetitionForm({ ...competitionForm, nom: e.target.value })}
              fullWidth
              required
              error={!competitionForm.nom && editingCompetition}
              helperText={!competitionForm.nom && editingCompetition ? t('competitions.competitionsTab.requiredFieldsError') : ""}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required error={!competitionForm.niveau_id && editingCompetition}>
              <InputLabel>{t('competitions.competitionsTab.level')}</InputLabel>
              <Select
                value={competitionForm.niveau_id}
                onChange={(e) => setCompetitionForm({ ...competitionForm, niveau_id: e.target.value })}
                label={t('competitions.competitionsTab.level')}
              >
                {niveaux.map((niveau) => (
                  <MenuItem key={niveau.id} value={niveau.id}>
                    {niveau.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              label={t('competitions.competitionsTab.eurobasketUrl')}
              value={competitionForm.eurobasket_url}
              onChange={(e) => setCompetitionForm({ ...competitionForm, eurobasket_url: e.target.value })}
              fullWidth
              placeholder="https://www.eurobasket.com/..."
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Stack direction="row" spacing={1} alignItems="center">
              <input
                accept="image/*"
                id="competition-logo-upload"
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => handleLogoChange(e, 'competition')}
              />
              <label htmlFor="competition-logo-upload">
                <IconButton color="primary" component="span">
                  <PhotoCamera />
                </IconButton>
              </label>
              {competitionLogoPreview && (
                <>
                  <Avatar
                    src={competitionLogoPreview}
                    sx={{ width: 40, height: 40 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      setCompetitionLogo(null);
                      setCompetitionLogoPreview(null);
                    }}
                  >
                    <Clear />
                  </IconButton>
                </>
              )}
              {editingCompetition && !competitionLogoPreview && (
                <Avatar
                  src={getLogoUrl(editingCompetition.id, 'competitions')}
                  sx={{ width: 40, height: 40 }}
                >
                  {editingCompetition.nom.charAt(0)}
                </Avatar>
              )}
              <Button
                variant="contained"
                onClick={editingCompetition ? handleUpdateCompetition : handleAddCompetition}
                sx={{ backgroundColor: '#ff9800', height: '56px' }}
                fullWidth
              >
                {editingCompetition ? t('competitions.competitionsTab.update') : t('competitions.competitionsTab.add')}
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {/* Filtros para competiciones */}
        <Card sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <FilterList sx={{ color: '#ff9800' }} />
            <TextField
              label={t('competitions.competitionsTab.searchPlaceholder')}
              value={competitionTextFilter}
              onChange={(e) => setCompetitionTextFilter(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, maxWidth: 300 }}
            />
            <Autocomplete
              multiple
              options={niveaux}
              getOptionLabel={(option) => option.nom}
              value={niveaux.filter(n => competitionNiveauFilter.includes(n.id))}
              onChange={(_, newValue) => setCompetitionNiveauFilter(newValue.map(n => n.id))}
              renderInput={(params) => (
                <TextField {...params} label={t('competitions.competitionsTab.filterByLevels')} size="small" />
              )}
              sx={{ minWidth: 250 }}
              size="small"
            />
          </Stack>
        </Card>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('competitions.competitionsTab.logo')}</TableCell>
                <TableCell>{t('competitions.competitionsTab.competition')}</TableCell>
                <TableCell>{t('competitions.competitionsTab.level')}</TableCell>
                <TableCell align="right">{t('competitions.competitionsTab.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCompetitions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      {t('competitions.competitionsTab.noCompetitionsFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCompetitions.map((competition) => (
                  <TableRow key={competition.id}>
                    <TableCell>
                      <Avatar
                        src={getLogoUrl(competition.id, 'competitions')}
                        sx={{ width: 40, height: 40 }}
                      >
                        {competition.nom.charAt(0)}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{competition.nom}</span>
                        {competition.eurobasket_url && (
                          <IconButton
                            size="small"
                            href={competition.eurobasket_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={competition.eurobasket_url}
                            sx={{ color: '#ff9800' }}
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip label={competition.niveau_nom} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => {
                          setEditingCompetition(competition);
                          setCompetitionForm({
                            nom: competition.nom,
                            niveau_id: competition.niveau_id,
                            eurobasket_url: competition.eurobasket_url || ''
                          });
                          setCompetitionLogoPreview(null);
                        }}
                        sx={{ color: '#ff9800' }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => setConfirmDelete({ type: 'competition', item: competition })}
                        sx={{ color: 'red' }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderEquipesTab = () => {
    const filteredEquipes = getFilteredEquipes();

    return (
      <Box>
        <Card sx={{ mb: 4, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {editingEquipe ? t('competitions.teams.editTeam') : t('competitions.teams.addTeam')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField
                label={t('competitions.teams.name')}
                value={equipeForm.nom}
                onChange={(e) => setEquipeForm({ ...equipeForm, nom: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Autocomplete
                multiple
                options={competitions}
                getOptionLabel={(option) => `${option.nom} (${option.niveau_nom})`}
                groupBy={(option) => option.niveau_nom}
                value={competitions.filter(c => equipeForm.competition_ids.includes(c.id))}
                onChange={(_, newValue) => setEquipeForm({
                  ...equipeForm,
                  competition_ids: newValue.map(c => c.id)
                })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('competitions.teams.competitionsRequired')}
                    error={equipeForm.competition_ids.length === 0}
                    helperText={equipeForm.competition_ids.length === 0 ? t('competitions.teams.selectAtLeastOne') : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <input
                  accept="image/*"
                  id="equipe-logo-upload"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={(e) => handleLogoChange(e, 'equipe')}
                />
                <label htmlFor="equipe-logo-upload">
                  <IconButton color="primary" component="span">
                    <PhotoCamera />
                  </IconButton>
                </label>
                {equipeLogoPreview && (
                  <>
                    <Avatar
                      src={equipeLogoPreview}
                      sx={{ width: 40, height: 40 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEquipeLogo(null);
                        setEquipeLogoPreview(null);
                      }}
                    >
                      <Clear />
                    </IconButton>
                  </>
                )}
                {editingEquipe && !equipeLogoPreview && (
                  <Avatar
                    src={getLogoUrl(editingEquipe.id, 'equipes')}
                    sx={{ width: 40, height: 40 }}
                  >
                    {editingEquipe.nom.charAt(0)}
                  </Avatar>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  onClick={editingEquipe ? handleUpdateEquipe : handleAddEquipe}
                  sx={{ backgroundColor: '#ff9800' }}
                >
                  {editingEquipe ? t('competitions.teams.update') : t('competitions.teams.add')}
                </Button>
                {editingEquipe && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setEditingEquipe(null);
                      setEquipeForm({ nom: '', competition_ids: [] });
                      setEquipeLogo(null);
                      setEquipeLogoPreview(null);
                    }}
                  >
                    {t('competitions.teams.cancel')}
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Card>

        {/* Bouton d'import */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<CloudDownload />}
            onClick={() => {
              setImportMode('equipes');
              setImportDialogOpen(true);
            }}
            sx={{ borderColor: '#ff9800', color: '#ff9800' }}
          >
            {t('competitions.import.importEquipes')}
          </Button>
        </Box>

        {/* Filtros para equipos */}
        <Card sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <FilterList sx={{ color: '#ff9800' }} />
            <TextField
              label={t('competitions.teams.searchPlaceholder')}
              value={equipeTextFilter}
              onChange={(e) => setEquipeTextFilter(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, maxWidth: 300 }}
            />
            <Autocomplete
              multiple
              options={competitions}
              getOptionLabel={(option) => `${option.nom} (${option.niveau_nom})`}
              groupBy={(option) => option.niveau_nom}
              value={competitions.filter(c => equipeCompetitionFilter.includes(c.id))}
              onChange={(_, newValue) => setEquipeCompetitionFilter(newValue.map(c => c.id))}
              renderInput={(params) => (
                <TextField {...params} label={t('competitions.teams.filterByCompetitions')} size="small" />
              )}
              sx={{ minWidth: 300 }}
              size="small"
            />
          </Stack>
        </Card>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('competitions.teams.logo')}</TableCell>
                <TableCell>{t('competitions.teams.team')}</TableCell>
                <TableCell>{t('competitions.teams.competitions')}</TableCell>
                <TableCell align="right">{t('competitions.teams.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEquipes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      {t('competitions.teams.noTeamsFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEquipes.map((equipe) => (
                  <TableRow key={equipe.id}>
                    <TableCell>
                      <Avatar
                        src={getLogoUrl(equipe.id, 'equipes')}
                        sx={{ width: 40, height: 40 }}
                      >
                        {equipe.nom.charAt(0)}
                      </Avatar>
                    </TableCell>
                    <TableCell>{equipe.nom}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                        {equipe.competitions ? (
                          equipe.competitions.split(', ').map((comp, idx) => (
                            <Chip key={idx} label={comp} size="small" />
                          ))
                        ) : (
                          <em style={{ color: '#ccc' }}>{t('competitions.teams.noCompetitions')}</em>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => {
                          setEditingEquipe(equipe);
                          const currentCompIds = equipeCompetitions
                            .filter(ec => ec.equipe_id === equipe.id)
                            .map(ec => ec.competition_id);
                          setEquipeForm({ 
                            nom: equipe.nom,
                            competition_ids: currentCompIds
                          });
                          setEquipeLogoPreview(null);
                        }}
                        sx={{ color: '#ff9800' }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => setConfirmDelete({ type: 'equipe', item: equipe })}
                        sx={{ color: 'red' }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // =============== RENDER PRINCIPAL ===============

  return (
    <Box sx={{ p: isSmallScreen ? 2 : 4 }}>
      
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3 }}
      >
        <Tab label={t('competitions.tabs.levels')} />
        <Tab label={t('competitions.tabs.competitions')} />
        <Tab label={t('competitions.tabs.teams')} />
      </Tabs>

      {activeTab === 0 && renderNiveauxTab()}
      {activeTab === 1 && renderCompetitionsTab()}
      {activeTab === 2 && renderEquipesTab()}

      {/* Dialog de confirmación */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t('competitions.delete.confirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('competitions.delete.confirmMessage', { name: confirmDelete?.item?.nom })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>{t('competitions.delete.cancel')}</Button>
          <Button
            onClick={() => {
              if (confirmDelete.type === 'niveau') handleDeleteNiveau(confirmDelete.item);
              if (confirmDelete.type === 'competition') handleDeleteCompetition(confirmDelete.item);
              if (confirmDelete.type === 'equipe') handleDeleteEquipe(confirmDelete.item);
            }}
            sx={{ color: 'red' }}
          >
            {t('competitions.delete.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensajes de éxito */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage('')}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Dialog d'import depuis la référence */}
      <ImportFromReference
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        mode={importMode}
        niveaux={niveaux}
        competitions={competitions}
        onImportComplete={() => {
          setImportDialogOpen(false);
          fetchCompetitions();
          fetchEquipes();
          fetchEquipeCompetitions();
          fetchJoueurs();
          setSuccessMessage(t('competitions.import.successMessage'));
        }}
      />
    </Box>
  );
};

export default CompetitionsManagement;